import React, { useEffect, useRef, useState } from 'react';
  import chevronLeftSmall from '../../../../public/assets/images/chevronleftsmall.svg';
  import datepickerIcon from '../../../../public/assets/images/calendarsingledatesolid.svg';
  import chevronRightSmall from '../../../../public/assets/images/chevronrightsmall.svg';
  import lockIcon from '../../../../public/assets/images/lockSolid.svg';
  import './datepicker.css';

  interface Props {
    name: string;
    label?: string;
    value?: string;
    required?: boolean;
    helperText?: string;
    disabled?: boolean;
    error?: string;
    onChange: (name: string, value: string) => void;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onDateSelected?: (name: string, value: string) => void; // Called when date is selected from calendar
  }

  
  const CustomDatepicker: React.FC<Props> = ({ name, label, value = '', required, helperText, disabled, error, onChange, onBlur, onDateSelected }) => {
    
    const today = new Date();
    const parseValue = (v: string) => {
      // expect MM/DD/YYYY
      const parts = v.split('/').map((p) => Number.parseInt(p, 10));
      if (parts.length === 3 && !Number.isNaN(parts[0]) && !Number.isNaN(parts[1]) && !Number.isNaN(parts[2])) {
           // Ensure year is 4 digits - if it's less, use current year
        const yearPart = parts[2];
        const validYear = yearPart >= 1000 && yearPart <= 9999 ? yearPart : today.getFullYear();
        return { month: parts[0] - 1, year: validYear };
      }
      return { month: today.getMonth(), year: today.getFullYear() };
    };

    const initial = parseValue(value);
    const [open, setOpen] = useState(false);
    const [month, setMonth] = useState(initial.month);
    const [year, setYear] = useState(initial.year);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const calendarRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      const measure = () => {
        // Trigger a layout recalculation to ensure wrapper width is accurate when calendar opens
        requestAnimationFrame(() => {
          // We'll use wrapperRef directly when calendar opens
        });
      };

      // Run on mount and on resize
      measure();
      window.addEventListener('resize', measure);
      return () => {
        window.removeEventListener('resize', measure);
      };
    }, []);

    useEffect(() => {
      const onDocClick = (ev: MouseEvent) => {
        const t = ev.target as Node | null;
        if (!t || !open) return;
        if (wrapperRef.current && (wrapperRef.current === t || wrapperRef.current.contains(t))) return;
        setOpen(false);
      };
      document.addEventListener('click', onDocClick);
      return () => document.removeEventListener('click', onDocClick);
    }, [open]);

    useEffect(() => {
      if (!open) return undefined;
      const onKey = (ev: KeyboardEvent) => {
        if (ev.key === 'Escape') {
          setOpen(false);
          if (inputRef.current) inputRef.current.focus();
        }
        if (ev.key === 'ArrowLeft') {
          ev.preventDefault();
          changeMonth(-1);
        }
        if (ev.key === 'ArrowRight') {
          ev.preventDefault();
          changeMonth(1);
        }
      };
      document.addEventListener('keydown', onKey);
      return () => document.removeEventListener('keydown', onKey);
    }, [open]);

    useEffect(() => {
      const parsed = parseValue(value);
      setMonth(parsed.month);
      setYear(parsed.year);
    }, [value]);

    const pad2 = (n: number) => String(n).padStart(2, '0');
    const formatMMDDYYYY = (yy: number, mm: number, dd: number) => `${pad2(mm + 1)}/${pad2(dd)}/${yy}`;

    const changeMonth = (delta: number) => {
      let m = month + delta;
      let y = year;
      if (m < 0) { m = 11; y -= 1; }
      if (m > 11) { m = 0; y += 1; }
      setMonth(m); setYear(y);
    };

    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    // Build calendar cells including leading disabled days from previous month
    type Cell = { day: number; inMonth: boolean };
    const cells: Cell[] = [];
    // previous month last day
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    // add previous month trailing days as disabled (so calendar shows full weeks)
    for (let i = 0; i < firstDay; i++) {
      const dayNum = prevMonthLastDay - (firstDay - 1) + i;
      cells.push({ day: dayNum, inMonth: false });
    }
    for (let d = 1; d <= daysInMonth; d++) cells.push({ day: d, inMonth: true });

    // When calendar opens, measure and set the wrapper width so calendar matches input
    useEffect(() => {
      if (!open || !calendarRef.current || !wrapperRef.current) return;
      const measure = () => {
        requestAnimationFrame(() => {
          // Guard against null refs in test environments or after unmount
          if (!wrapperRef.current || !calendarRef.current) return;
          const wrapperWidth = Math.round((wrapperRef.current as HTMLElement).offsetWidth);
          if (wrapperWidth > 0) {
            calendarRef.current.style.width = `${wrapperWidth}px`;
            calendarRef.current.style.setProperty('--calendar-width', `${wrapperWidth}px`);
          }
        });
      };
      measure();
    }, [open]);

    return (
      <div className="ds-form w-full" data-ds-theme="vessel" data-ds-variant="input" data-ds-version="1">
        {label && <label htmlFor={`date-input-${name}`}>{label}</label>}
        <div className="w-full min-w-[300px] date-field-wrapper" ref={wrapperRef}>
          <div className="input-with-icon">
            <input
              id={`date-input-${name}`}
              name={name}
              ref={inputRef}
              type="text"
              className={`w-full min-w-[300px] block date-input-field ${disabled ? "ds-form__disabled-field bg-gray-100 text-gray-600" : ""} ${error ? 'field-error' : ''}`}
              value={value}
              onChange={(e) => onChange(name, e.target.value)}
              onBlur={onBlur}
              onKeyDown={(e) => {
                // open calendar on Enter, Space or ArrowDown from input
                const k = e.key;
                if (k === 'Enter' || k === 'ArrowDown' || k === ' ' || k === 'Spacebar' || k === 'Space') {
                  e.preventDefault();
                  setOpen(true);
                }
              }}
              placeholder="i.e. 10/07/2025"
              required={required}
              disabled={disabled}
            />
            <button
              type="button"
              aria-label={disabled ? `Date field is locked for ${label ?? name}` : `Open calendar for ${label ?? name}`}
              onClick={() => !disabled && setOpen((s) => !s)}
              aria-expanded={open}
              disabled={disabled}
              className="date-input-icon"
            >
              <img src={disabled ? lockIcon : datepickerIcon} alt={disabled ? "locked" : "calendar"} />
            </button>
          </div>

          {open && !disabled && (
            <dialog
              id={`custom-calendar-${name}`}
              ref={calendarRef as React.RefObject<HTMLDialogElement>}
              className="custom-calendar bg-white border rounded shadow-lg p-3 absolute top-12 left-0"
              aria-label={`Calendar for ${label ?? name}`}
              open={open}
            >
              <div className="cal-header flex justify-between items-center mb-2">
                <button type="button" onClick={() => changeMonth(-1)} aria-label="Previous month" className="p-1">
                  <img src={chevronLeftSmall} alt="Previous month" className="w-4 h-4" />
                </button>
                <div>{new Date(year, month).toLocaleString(undefined, { month: 'long' })} {year}</div>
                <button type="button" onClick={() => changeMonth(1)} aria-label="Next month" className="p-1">
                  <img src={chevronRightSmall} alt="Next month" className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center text-sm">
                {['Su','Mo','Tu','We','Th','Fr','Sa'].map((d) => <div key={d} className="font-medium">{d}</div>)}
                {cells.map((c) => {
                  // c: { day: number; inMonth: boolean }
                  const uniqueKey = c.inMonth ? `${year}-${month}-${c.day}` : `disabled-${year}-${month}-${c.day}`;
                  
                  if (!c.inMonth) {
                    return (
                      <div key={uniqueKey} className="day">
                        <div className="disabled-day" aria-hidden="true">{c.day}</div>
                      </div>
                    );
                  }

                  const dayVal = formatMMDDYYYY(year, month, c.day);
                  const isSelected = value === dayVal;

                  return (
                    <div key={uniqueKey} className={`day cursor-pointer`}>
                      <button
                        type="button"
                        aria-pressed={isSelected}
                        className={`w-9 h-9 rounded-full flex items-center justify-center ${isSelected ? 'bg-blue-600 text-white' : 'hover:bg-gray-100'}`}
                        onClick={() => {
                          const val = dayVal;
                          onChange(name, val);
                          setOpen(false);
                          // Call onDateSelected callback - calendar selection only, not typing
                          if (onDateSelected) {
                            setTimeout(() => onDateSelected(name, val), 0);
                          }
                        }}
                      >
                        {c.day}
                      </button>
                    </div>
                  );
                })}
              </div>
            </dialog>
          )}

        </div>
        {error ?  <div className="error-description">
    <ul id="text-input-error-description-list-1">
      <li>
        <img src="/assets/images/errorCircleSmall.svg" alt="Error icon" width="14" height="14" />
        <span className="screenreader-only">Error</span>
        {error}
        
      </li>
    </ul>
  </div>: <span className="ds-form__text-input-helper">{helperText}</span>}
      </div>
    );
  };

  export default CustomDatepicker;
